import React, {useState, useEffect, useCallback, useContext} from 'react'
import {useHttp} from "../hooks/http.hook"
import {
    getAuthHeader,
    getItems,
    getLocalStorageItem,
    getPathAndQueryParams,
    objectLang, removeLocalStorageItem,
    setCookieOfFolder,
    setErrorWindow,
    setImagesDataToSlider,
    setLocalStorageItem
} from "../functions"
import config from '../config'
import {AuthContext} from '../context/AuthContext'
import {LoaderClass} from "../elements/LoaderClass"
import {ModalWin} from "../elements/ModalWin"
import {LinksClass} from "../components/LinksClass"
import {useNavigate, useParams} from "react-router-dom"
import {PathLineClass} from "../elements/PathLineClass"

export const GoogleDrivePage = () => {
    setLocalStorageItem('page', 'GoogleDrivePage')
    const auth = useContext(AuthContext)
    const {request, loading} = useHttp()
    const [allowed, setAllowed] = useState(true)
    const [documents, setDocuments] = useState(null)
    const [items, setItems] = useState([])
    const [quota, setQuota] = useState(null)
    const [dataModal, setDataModal] = useState({})
    const [pageToken, setPageToken] = useState(null)
    const [folders, setFolders] = useState([])
    const [links, setLinks] = useState([])
    const [countItems, setCountItems] = useState(0)
    const [itemId, setItemId] = useState(null)
    const [thumbnails, setThumbnails] = useState({})
    const [linksView, setLinksView] = useState({})
    const [isLoadingThumb, setIsLoadingThumb] = useState(false)
    const navigate = useNavigate()
    const {id: folderId} = useParams()

    const host =
        window.location.host === 'localhost:3000'
            ? config.localHost
            : config.onrender

    const MAX_ITEMS = 1000
    const sort = getLocalStorageItem(`sort-G-${folderId}`) || 'type'
    const order = getLocalStorageItem(`order-G-${folderId}`) || 'asc'

    // --- Загрузка контента Google Drive ---
    const loadContent = useCallback(
        async (pageToken = null) => {
            const lang = objectLang().language
            const l = lang.lang
            try {
                const data = await request(
                    `${host}/api/google-drive/content`,
                    'POST',
                    {
                        parentId: folderId,
                        pageToken: pageToken,
                        type: 'all',
                        MAX_ITEMS: MAX_ITEMS,
                        sort: sort,
                        order: order,
                        URI: window.location.origin,
                        lang: l
                    },
                    {
                        Authorization: getAuthHeader(auth),
                        'Content-Type': 'application/json',
                    }
                )

                if (data.status > 201 && data.code !== 6042) {
                    setAllowed(false)
                    setDataModal(setErrorWindow(
                        lang.modal.errors.title,
                        data.code,
                        lang.messages.code_541,
                        lang.modal.buttons.btnOK,
                        `replaceTo /${l}/`,
                        lang.modal.buttons.btnCancel,
                        `replaceTo ${getPathAndQueryParams()}`
                    ))
                    return
                }
                if (data['newAccessToken']) auth.accessToken = data['newAccessToken']
                if (!pageToken) {
                    setDocuments(data.documents)
                    setItems([])
                    setPageToken(data.nextPageToken || null)
                    setCountItems(data.documents.folders.length + data.documents.links.length + data.documents.notes.length)
                    setLocalStorageItem(`sort-G-${data.documents.parent}`, data.documents.sort)
                    setLocalStorageItem(`order-G-${data.documents.parent}`, data.documents.order)
                    setCookieOfFolder(data.documents)
                } else {
                    if (data.documents) {
                        setFolders(data.documents.folders)
                        setLinks(data.documents.links)
                        setPageToken(data.nextPageToken || null)
                        setItemId(data.documents.parent)
                        setCountItems(countItems + data.documents.folders.length + data.documents.links.length + data.documents.notes.length)
                        setLocalStorageItem(`sort-G-${data.documents.parent}`, data.documents.sort)
                        setLocalStorageItem(`order-G-${data.documents.parent}`, data.documents.order)
                        setCookieOfFolder(data.documents)
                        setLocalStorageItem(`pageToken-${data.documents.parent}`, data.nextPageToken || null)
                    } else {
                        setPageToken(null)
                    }
                }

            } catch (err) {
                console.error('Ошибка загрузки контента:', err)
            }
        },
        [auth, request, host, folderId, order, sort]
    )

    // --- Загрузка миниатюр И линков картинок---
    const loadThumbnails = useCallback(async () => {
        setIsLoadingThumb(true)
        try {
            const thumbnailsData = await request(
                `${host}/api/google-drive/thumbnails`,
                'POST',
                {
                    parentId: folderId,
                    pageToken: pageToken,
                    MAX_ITEMS: MAX_ITEMS,
                    URI: window.location.origin
                },
                {Authorization: getAuthHeader(auth), 'Content-Type': 'application/json'}
            )
            if (thumbnailsData.status === 200) {
                setThumbnails(thumbnailsData.thumbnails)
                setLinksView(thumbnailsData.linksView)
                if (thumbnailsData['newAccessToken']) auth.accessToken = thumbnailsData['newAccessToken']
            }
        } catch (e) {
            console.error('Ошибка загрузки миниатюр:', e)
        }
    }, [auth, host, request, setThumbnails, folderId, pageToken])

    // --- Загрузка квоты ---
    const loadQuota = useCallback(async () => {
        try {
            const quotaData = await request(
                `${host}/api/google-drive/quota`,
                'POST',
                {redirectURI: window.location.origin},
                {Authorization: getAuthHeader(auth), 'Content-Type': 'application/json'}
            )
            if (quotaData['newAccessToken']) auth.accessToken = quotaData['newAccessToken']
            setQuota(quotaData)
        } catch (e) {
            console.error('Ошибка загрузки квоты:', e)
        }
    }, [auth, host, request])

    const loadMore = async () => {
        await loadContent(pageToken)
    }

    const clearTrashHandler = useCallback(async () => {
        try {
            await request(
                `${host}/api/google-drive/empty-trash`,
                'DELETE',
                {redirectURI: window.location.origin},
                {Authorization: getAuthHeader(auth), 'Content-Type': 'application/json'}
            )
            removeLocalStorageItem('trash')
            window.location.replace(getPathAndQueryParams())
        } catch (e) {
            console.error('Ошибка очистки корзины:', e)
        }
    }, [auth, host, request])

    // --- Основная загрузка при монтировании ---
    useEffect(() => {
        if (itemId === folderId) {
            loadContent(pageToken).then()
        } else {
            if (!quota) {
                loadQuota().then()
            }
            setFolders([])
            setLinks([])
            setPageToken(null)
            const docsLocal = getLocalStorageItem(folderId)
            if (docsLocal && docsLocal.sort === sort && docsLocal.order === order) {
                setLocalStorageItem('currentParent', docsLocal.parent)
                setTimeout(() => {
                    setDocuments(docsLocal)
                    setItems([])
                    setCountItems(docsLocal.folders.length + docsLocal.links.length + docsLocal.notes.length)
                    setLocalStorageItem(`sort-${docsLocal.parent}`, docsLocal.sort)
                    setLocalStorageItem(`order-${docsLocal.parent}`, docsLocal.order)
                }, 100)
                if (document.getElementById('filterInput')) {
                    document.getElementById('filterInput').value = ''
                    const arr = Array.from(document.getElementById('items').children)
                    for (const i in arr) {
                        arr[i].classList.remove("d-none")
                    }
                }
                if (folderId !== 'trash') {
                    loadThumbnails().then(() => {
                        setIsLoadingThumb(false)
                    })
                }
            } else {
                loadContent().then(() => {
                    if (folderId !== 'trash') {
                        loadThumbnails().then(() => {
                            setIsLoadingThumb(false)
                        })
                    }
                })
            }
        }
    }, [loadContent, loadQuota, folderId, itemId, setIsLoadingThumb, quota, loadThumbnails, pageToken, sort, order])

    useEffect(() => {
        if (!items.length) return
        if (!Object.keys(linksView).length) return

        const enriched = items.map(item => {
            if (item.type === 'google-file') {
                return {
                    ...item,
                    linkViewImage: linksView[item.id] || '',
                    thumbUrl: thumbnails[item.id] || ''
                }
            }
            return item
        })

        setImagesDataToSlider(enriched)
    }, [items, thumbnails, linksView])

    // --- Отрисовка ---
    if (!allowed) return <ModalWin data={dataModal}/>

    if (documents) {
        documents.googleDrive = true // Мы открываем гугл драйв
        const f = documents.folders
        const l = documents.links
        for (const i in folders) {
            f.push(folders[i])
        }
        for (const i in links) {
            l.push(links[i])
        }
        documents.folders = f
        documents.links = l
        documents.quota = quota

        if (items.length === 0) {
            setItems(getItems(documents))
        }

        if (loading && !isLoadingThumb) {
            return (
                <>
                    <PathLineClass documents={documents}/>
                    <LoaderClass position={'fixed'} top={120} left={'calc(50% - 24px)'}/>
                </>
            )
        }
    }

    return (
        <>
            <PathLineClass documents={documents}/>
            <LinksClass
                quota={quota}
                auth={auth}
                documents={documents}
                items={items}
                rootFolder={auth.rootFolder}
                loadMore={loadMore}
                pageToken={!!pageToken}
                setCountItems={setCountItems}
                countItems={countItems}
                thumbnails={thumbnails}
                linksView={linksView}
                navigate={navigate}
                clearTrashHandler={clearTrashHandler}
            />
        </>
    )
}
